const R_EARTH_KM = 6371;
const toRad = (d: number): number => (d * Math.PI) / 180;
const toDeg = (r: number): number => (r * 180) / Math.PI;

/** Great-circle distance in metres. */
export function haversineM(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R_EARTH_KM * Math.asin(Math.sqrt(h)) * 1000;
}

/** Initial bearing from A to B in degrees [0, 360). */
export function bearingDeg(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const la1 = toRad(aLat);
  const la2 = toRad(bLat);
  const dLon = toRad(bLon - aLon);
  const y = Math.sin(dLon) * Math.cos(la2);
  const x = Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** Signed smallest difference between two bearings, in (-180, 180]. */
export function bearingDiff(a: number, b: number): number {
  return ((b - a + 540) % 360) - 180;
}

/** Project a point `distKm` along `bearing` (degrees) from (lat, lon). */
export function project(lat: number, lon: number, bearing: number, distKm: number): { lat: number; lon: number } {
  const d = distKm / R_EARTH_KM;
  const br = toRad(bearing);
  const la1 = toRad(lat);
  const lo1 = toRad(lon);
  const la2 = Math.asin(Math.sin(la1) * Math.cos(d) + Math.cos(la1) * Math.sin(d) * Math.cos(br));
  const lo2 =
    lo1 + Math.atan2(Math.sin(br) * Math.sin(d) * Math.cos(la1), Math.cos(d) - Math.sin(la1) * Math.sin(la2));
  return { lat: toDeg(la2), lon: ((toDeg(lo2) + 540) % 360) - 180 };
}

export interface Tile {
  lat: number;
  lon: number;
  radiusM: number;
}

export const GEOSEARCH_MAX_RADIUS_M = 10_000;

/**
 * Wikipedia caps gsradius at 10 km, and highways curve, so a straight projection misses.
 * Emit a fan: the origin, points along the heading every ~9 km out to `lookaheadKm`,
 * and two flank tiles at the far end swung ±35°. Stationary (no heading): one circle.
 */
export function corridorTiles(lat: number, lon: number, heading: number | null, lookaheadKm = 20): Tile[] {
  const r = GEOSEARCH_MAX_RADIUS_M;
  if (heading == null || Number.isNaN(heading)) return [{ lat, lon, radiusM: r }];

  const tiles: Tile[] = [{ lat, lon, radiusM: r }];
  const step = 9;
  let d = step;
  for (; d <= lookaheadKm; d += step) {
    const p = project(lat, lon, heading, d);
    tiles.push({ lat: p.lat, lon: p.lon, radiusM: r });
  }
  const far = Math.max(step, d - step);
  for (const off of [-35, 35]) {
    const p = project(lat, lon, (heading + off + 360) % 360, far);
    tiles.push({ lat: p.lat, lon: p.lon, radiusM: r });
  }
  return tiles;
}

/** 0.1 degree cell (~11 km N/S). Keyed so repeat visitors hit the crawl cache. */
export const tileKey = (lat: number, lon: number): string =>
  `${(Math.round(lat * 10) / 10).toFixed(1)},${(Math.round(lon * 10) / 10).toFixed(1)}`;

export interface BBox {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
}

/** Rough bounding box in degrees for a spatial lookup. */
export function bbox(lat: number, lon: number, km: number): BBox {
  const dLat = km / 111;
  const dLon = km / (111 * Math.max(0.1, Math.cos(toRad(lat))));
  return { minLat: lat - dLat, maxLat: lat + dLat, minLon: lon - dLon, maxLon: lon + dLon };
}

export const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));
